import { useMemo } from "react";

import categoriesService from "../services/categories-service";
import filtersService from "../services/filters-service";
import type { FilterCategory } from "../types/category";
import type { SearchFilterInstance } from "../types/searchFilterInstance";

/**
 * Local-state equivalent of `useVisibleFilters`: same pure `filtersService.getVisibleFilters`
 * underneath, but `selectedCategory` and the "category panel expanded" flag come from
 * props/local state rather than Redux.
 */
type Params<TFilter extends SearchFilterInstance> = {
    filtersConfig: readonly TFilter[];
    selectedCategory: FilterCategory;
    searchMatchedFilters: readonly TFilter[] | undefined;
    isSearchActive: boolean;
};

type Result<TFilter extends SearchFilterInstance> = {
    readonly filterList: readonly TFilter[];
    readonly categoriesWithHiddenFilters: ReadonlySet<FilterCategory>;
};

// This hook always renders the expanded view, where neither pin nor value state gates visibility.
const NO_KEYS: ReadonlySet<string> = new Set();

export const useLocalVisibleFilters = <TFilter extends SearchFilterInstance = SearchFilterInstance>({
    filtersConfig,
    selectedCategory,
    searchMatchedFilters,
    isSearchActive,
}: Params<TFilter>): Result<TFilter> => {
    const visibleFilters = useMemo(
        () =>
            filtersService.getVisibleFilters(
                {
                    filtersConfig,
                    selectedFilterKeys: NO_KEYS,
                    dirtyFilterKeys: NO_KEYS,
                    searchMatchedFilters,
                    isSearchActive,
                },
                {
                    isCategoryPanelExpanded: true,
                    selectedCategory,
                },
            ) as readonly TFilter[],
        [filtersConfig, searchMatchedFilters, isSearchActive, selectedCategory],
    );

    const categoriesWithHiddenFilters = useMemo<ReadonlySet<FilterCategory>>(
        () =>
            categoriesService.getCategoriesWithHiddenFilters({
                filtersList: filtersConfig,
                isFilterHidden: () => false,
            }),
        [filtersConfig],
    );

    return {
        filterList: visibleFilters,
        categoriesWithHiddenFilters,
    };
};
